/**
 * Campaign Tracking Utilities
 * Handles UTM parameter parsing and conversion tracking
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// UTM PARAMETER HANDLING
// ============================================

/**
 * Parse UTM parameters from URL
 */
export const parseUTMParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  
  return {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_content: urlParams.get('utm_content'),
    utm_term: urlParams.get('utm_term')
  };
};

/**
 * Store UTM params in session storage for later attribution
 */
export const storeUTMParams = () => {
  const params = parseUTMParams();
  
  // Only store if we have campaign data
  if (params.utm_campaign) {
    sessionStorage.setItem('shs_utm_params', JSON.stringify({
      ...params,
      landing_page: window.location.pathname,
      timestamp: new Date().toISOString()
    }));
    
    // Track the initial click
    trackAdClick(params.utm_campaign, params.utm_source || 'direct');
  }
};

/**
 * Get stored UTM params
 */
export const getStoredUTMParams = () => {
  try {
    const stored = sessionStorage.getItem('shs_utm_params');
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    return null;
  }
};

/**
 * Get campaign session ID
 */
export const getCampaignSessionId = () => {
  let sessionId = sessionStorage.getItem('shs_campaign_session_id');
  if (!sessionId) {
    sessionId = `camp_sess_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    sessionStorage.setItem('shs_campaign_session_id', sessionId);
  }
  return sessionId;
};

// ============================================
// CLICK TRACKING
// ============================================

/**
 * Track an ad click
 */
export const trackAdClick = async (campaignId, source = 'direct') => {
  if (!campaignId) return;
  
  const utmParams = parseUTMParams();
  
  try {
    await fetch(`${API_URL}/api/campaigns/track/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        campaign_id: campaignId,
        source: source,
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        utm_content: utmParams.utm_content,
        landing_page: window.location.pathname,
        session_id: getCampaignSessionId(),
        timestamp: new Date().toISOString()
      })
    });
  } catch (e) {
    console.log('[CAMPAIGN CLICK]', campaignId, source);
  }
};

// ============================================
// CONVERSION TRACKING
// ============================================

/**
 * Conversion types and their values
 */
export const CONVERSION_VALUES = {
  assessment_started: 0,
  assessment_completed: 1.00,
  cpap_quiz_started: 0,
  cpap_quiz_completed: 0.50,
  cpap_product_clicked: 2.00,  // Affiliate potential
  ai_chat_started: 0.25,
  ai_chat_extended: 0.50,
  signup_free: 0,
  signup_explorer: 2.00,
  signup_professional: 10.00,
  signup_institution: 50.00
};

/**
 * Track a conversion event
 */
export const trackConversion = async (conversionType, customValue = null) => {
  const utmParams = getStoredUTMParams();
  
  // If no campaign attribution, don't track as campaign conversion
  if (!utmParams?.utm_campaign) return;
  
  const value = customValue !== null ? customValue : (CONVERSION_VALUES[conversionType] || 0);
  
  try {
    await fetch(`${API_URL}/api/campaigns/track/conversion`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        campaign_id: utmParams.utm_campaign,
        session_id: getCampaignSessionId(),
        conversion_type: conversionType,
        conversion_value: value,
        timestamp: new Date().toISOString()
      })
    });
    
    console.log('[CAMPAIGN CONVERSION]', conversionType, value);
  } catch (e) {
    console.log('[CAMPAIGN CONVERSION ERROR]', e);
  }
};

// ============================================
// AUTO-INITIALIZATION
// ============================================

/**
 * Initialize campaign tracking on page load
 * Call this once in App.js or index.js
 */
export const initCampaignTracking = () => {
  // Store UTM params when page loads
  if (typeof window !== 'undefined') {
    storeUTMParams();
  }
};

// ============================================
// CONVENIENCE FUNCTIONS
// ============================================

/**
 * Track assessment start
 */
export const trackAssessmentStart = () => trackConversion('assessment_started');

/**
 * Track assessment completion
 */
export const trackAssessmentComplete = () => trackConversion('assessment_completed');

/**
 * Track CPAP quiz start
 */
export const trackCPAPQuizStart = () => trackConversion('cpap_quiz_started');

/**
 * Track CPAP quiz completion
 */
export const trackCPAPQuizComplete = () => trackConversion('cpap_quiz_completed');

/**
 * Track CPAP product click (affiliate)
 */
export const trackCPAPProductClick = () => trackConversion('cpap_product_clicked');

/**
 * Track AI chat start
 */
export const trackAIChatStart = () => trackConversion('ai_chat_started');

/**
 * Track extended AI chat (5+ messages)
 */
export const trackAIChatExtended = () => trackConversion('ai_chat_extended');

/**
 * Track signup by tier
 */
export const trackSignup = (tier) => trackConversion(`signup_${tier}`);

// ============================================
// LANDING PAGE HELPERS
// ============================================

/**
 * Check if current page is from a campaign
 */
export const isFromCampaign = () => {
  return !!getStoredUTMParams()?.utm_campaign;
};

/**
 * Get campaign landing context
 * Returns info useful for personalizing the landing experience
 */
export const getCampaignContext = () => {
  const params = getStoredUTMParams();
  if (!params) return null;
  
  return {
    campaignId: params.utm_campaign,
    source: params.utm_source,
    medium: params.utm_medium,
    landingPage: params.landing_page,
    isPaid: params.utm_medium === 'cpc' || params.utm_medium === 'paid'
  };
};

export default {
  parseUTMParams,
  storeUTMParams,
  getStoredUTMParams,
  trackAdClick,
  trackConversion,
  initCampaignTracking,
  isFromCampaign,
  getCampaignContext
};
